import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import AsyncSelect from 'react-select/async';
import { MultiValue } from 'react-select';
import axios from 'axios';
import { Modal, Button } from '@/components/ui';
import { selectStyles } from '@/components/utils/DataTable';
import {
  FilterOption,
  loadBrokerOptions,
  loadCompanyOptions,
  loadInvestmentStrategyOptions,
} from '@/components/utils/filters';
import { blockTradeApi } from '@/services/api';
import {
  BLOCK_TRADE_PARTY_TYPE_OPTIONS,
  BLOCK_TRADE_STATUS_OPTIONS,
  BlockTrade,
  BlockTradeCreatePayload,
  BlockTradePartyType,
  BlockTradeStatus,
  quotedPriceLabelFor,
} from '@/types/blockTrade';
import styles from './BlockTradeForm.module.css';

interface BlockTradeFormValues {
  company: FilterOption | null;
  broker: FilterOption | null;
  party_type: string;
  investment_strategies: FilterOption[];
  quantity: string;
  ask_price: string;
  trade_date: string;
  status: string;
}

interface BlockTradeFormModalProps {
  open: boolean;
  /** null → create mode (backend defaults the date to today and the status to Available). */
  blockTrade: BlockTrade | null;
  onClose: () => void;
  onSaved: (saved: BlockTrade) => void;
}

// Field-level messages the backend returns keyed by the same names this form uses,
// so a 400 lands on the offending input instead of a generic banner.
const FIELD_NAMES = [
  'company',
  'broker',
  'party_type',
  'investment_strategies',
  'quantity',
  'ask_price',
  'trade_date',
  'status',
] as const;

const emptyValues: BlockTradeFormValues = {
  company: null,
  broker: null,
  party_type: '',
  investment_strategies: [],
  quantity: '',
  ask_price: '',
  trade_date: '',
  status: '',
};

const toFormValues = (blockTrade: BlockTrade | null): BlockTradeFormValues => {
  if (!blockTrade) return emptyValues;
  return {
    company: {
      value: blockTrade.company.toString(),
      label: `${blockTrade.company_symbol} - ${blockTrade.company_name}`,
    },
    broker: { value: blockTrade.broker.toString(), label: blockTrade.broker_name },
    party_type: blockTrade.party_type.toString(),
    investment_strategies: blockTrade.investment_strategy_details.map(strategy => ({
      value: strategy.id.toString(),
      label: `${strategy.code} - ${strategy.name}`,
    })),
    quantity: blockTrade.quantity.toString(),
    ask_price: blockTrade.ask_price?.toString() ?? '',
    trade_date: blockTrade.trade_date,
    status: blockTrade.status.toString(),
  };
};

const BlockTradeFormModal: React.FC<BlockTradeFormModalProps> = ({
  open,
  blockTrade,
  onClose,
  onSaved,
}) => {
  const isEdit = blockTrade !== null;
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<BlockTradeFormValues>({ defaultValues: emptyValues });

  useEffect(() => {
    if (!open) return;
    setFormError(null);
    reset(toFormValues(blockTrade));
  }, [open, blockTrade, reset]);

  const todayLabel = useMemo(() => new Date().toISOString().split('T')[0], []);

  // The quote is named for the side it is offered on, so the price label follows the
  // Buyer/Seller choice as soon as it is made rather than after the row is saved.
  const partyType = useWatch({ control, name: 'party_type' });
  const priceLabel = quotedPriceLabelFor(
    partyType ? (Number(partyType) as BlockTradePartyType) : null,
  );

  const applyServerErrors = (error: unknown) => {
    if (!axios.isAxiosError(error) || !error.response) {
      setFormError('Failed to save block trade. Please try again.');
      return;
    }
    const data = error.response.data as Record<string, unknown> | undefined;
    if (!data || typeof data !== 'object') {
      setFormError('Failed to save block trade. Please try again.');
      return;
    }

    let matched = false;
    FIELD_NAMES.forEach(field => {
      const message = data[field];
      if (!message) return;
      matched = true;
      setError(field, {
        type: 'server',
        message: Array.isArray(message) ? String(message[0]) : String(message),
      });
    });

    if (!matched) {
      const detail = data.detail;
      setFormError(
        typeof detail === 'string' ? detail : 'Failed to save block trade. Please try again.',
      );
    }
  };

  const submit = handleSubmit(async values => {
    setFormError(null);

    // Both are `required` in the field rules above, so handleSubmit will not reach here
    // with either unset — this narrows the type without a non-null assertion.
    const { company, broker } = values;
    if (!company || !broker) return;

    // additional_details is deliberately absent from this form — the backend defaults it
    // to {} on create, and omitting it on update leaves whatever is already stored intact.
    // trade_date and status are only sent when the operator set them; on create the model
    // defaults supply today's date and Available.
    const payload: BlockTradeCreatePayload = {
      company: Number(company.value),
      broker: Number(broker.value),
      party_type: Number(values.party_type) as BlockTradePartyType,
      investment_strategies: values.investment_strategies.map(strategy => Number(strategy.value)),
      quantity: Number(values.quantity),
      ask_price: values.ask_price,
      ...(blockTrade && values.trade_date ? { trade_date: values.trade_date } : {}),
      ...(blockTrade && values.status
        ? { status: Number(values.status) as BlockTradeStatus }
        : {}),
    };

    try {
      const saved = blockTrade
        ? await blockTradeApi.updateBlockTrade(blockTrade.id, payload)
        : await blockTradeApi.createBlockTrade(payload);
      onSaved(saved);
    } catch (error) {
      applyServerErrors(error);
    }
  });

  return (
    <Modal
      open={open}
      title={isEdit ? 'Edit Block Trade' : 'Add Block Trade'}
      onClose={onClose}
      isSubmitting={isSubmitting}
      maxWidth={640}
      titleId="block-trade-form-title"
      footer={
        <>
          <Button
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            form="block-trade-form"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="block-trade-form" className={styles.form} onSubmit={submit} noValidate>
        {formError && <div className={styles['form-error']}>{formError}</div>}

        <div className={styles.field}>
          <label htmlFor="block-trade-company" className={styles.required}>Company</label>
          <Controller
            name="company"
            control={control}
            rules={{ required: 'Company is required.' }}
            render={({ field }) => (
              <AsyncSelect
                inputId="block-trade-company"
                value={field.value}
                onChange={option => field.onChange(option ?? null)}
                loadOptions={loadCompanyOptions}
                defaultOptions
                placeholder="Search company…"
                isClearable
                isDisabled={isSubmitting}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectStyles}
              />
            )}
          />
          {errors.company && <span className={styles['error-text']}>{errors.company.message}</span>}
        </div>

        <div className={styles.field}>
          <label htmlFor="block-trade-broker" className={styles.required}>Broker</label>
          <Controller
            name="broker"
            control={control}
            rules={{ required: 'Broker is required.' }}
            render={({ field }) => (
              <AsyncSelect
                inputId="block-trade-broker"
                value={field.value}
                onChange={option => field.onChange(option ?? null)}
                loadOptions={loadBrokerOptions}
                defaultOptions
                placeholder="Select broker…"
                isClearable
                isDisabled={isSubmitting}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectStyles}
              />
            )}
          />
          {errors.broker && <span className={styles['error-text']}>{errors.broker.message}</span>}
        </div>

        <div className={styles.field}>
          <label htmlFor="block-trade-party-type" className={styles.required}>Buyer / Seller</label>
          <select
            id="block-trade-party-type"
            className={errors.party_type ? styles['error-input'] : undefined}
            disabled={isSubmitting}
            {...register('party_type', { required: 'Buyer / Seller is required.' })}
          >
            <option value="">Select…</option>
            {BLOCK_TRADE_PARTY_TYPE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          {errors.party_type && (
            <span className={styles['error-text']}>{errors.party_type.message}</span>
          )}
        </div>

        <div className={`${styles.field} ${styles['field-full']}`}>
          <label htmlFor="block-trade-investment-strategies" className={styles.required}>
            Investment Strategies
          </label>
          <Controller
            name="investment_strategies"
            control={control}
            rules={{
              validate: selected =>
                selected.length > 0 || 'Select at least one investment strategy.',
            }}
            render={({ field }) => (
              <AsyncSelect
                inputId="block-trade-investment-strategies"
                isMulti
                value={field.value}
                onChange={(options: MultiValue<FilterOption>) =>
                  field.onChange(options ? [...options] : [])
                }
                loadOptions={loadInvestmentStrategyOptions}
                defaultOptions
                placeholder="Select strategies…"
                isClearable
                isDisabled={isSubmitting}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectStyles as never}
              />
            )}
          />
          <span className={styles.hint}>
            Decides whose holdings, AUM and desk mandate this block is measured against.
          </span>
          {errors.investment_strategies && (
            <span className={styles['error-text']}>{errors.investment_strategies.message}</span>
          )}
        </div>

        <div className={styles.field}>
          <label htmlFor="block-trade-quantity" className={styles.required}>Quantity</label>
          <input
            id="block-trade-quantity"
            type="number"
            step="1"
            min="1"
            placeholder="e.g. 250000"
            className={errors.quantity ? styles['error-input'] : undefined}
            disabled={isSubmitting}
            {...register('quantity', {
              required: 'Quantity is required.',
              validate: value => {
                const quantity = Number(value);
                if (!Number.isFinite(quantity) || quantity <= 0) {
                  return 'Quantity must be greater than zero.';
                }
                if (!Number.isInteger(quantity)) {
                  return 'Quantity must be a whole number of shares.';
                }
                return true;
              },
            })}
          />
          {errors.quantity && (
            <span className={styles['error-text']}>{errors.quantity.message}</span>
          )}
        </div>

        <div className={styles.field}>
          <label htmlFor="block-trade-ask-price" className={styles.required}>{priceLabel}</label>
          <input
            id="block-trade-ask-price"
            type="number"
            step="0.0001"
            min="0"
            placeholder="e.g. 1450.2500"
            className={errors.ask_price ? styles['error-input'] : undefined}
            disabled={isSubmitting}
            {...register('ask_price', {
              required: `${priceLabel} is required.`,
              validate: value =>
                Number(value) > 0 ? true : `${priceLabel} must be greater than zero.`,
            })}
          />
          {errors.ask_price && (
            <span className={styles['error-text']}>{errors.ask_price.message}</span>
          )}
        </div>

        {isEdit ? (
          <>
            <div className={styles.field}>
              <label htmlFor="block-trade-date">Date</label>
              <input
                id="block-trade-date"
                type="date"
                disabled={isSubmitting}
                {...register('trade_date')}
              />
              {errors.trade_date && (
                <span className={styles['error-text']}>{errors.trade_date.message}</span>
              )}
            </div>

            <div className={styles.field}>
              <label htmlFor="block-trade-status">Status</label>
              <select id="block-trade-status" disabled={isSubmitting} {...register('status')}>
                {BLOCK_TRADE_STATUS_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              {errors.status && (
                <span className={styles['error-text']}>{errors.status.message}</span>
              )}
            </div>

            <div className={`${styles.field} ${styles['field-full']}`}>
              <span className={styles.hint}>
                Saving re-captures the details — market, holdings and mandate figures are
                taken again as at now, replacing the ones the original decision was made on.
              </span>
            </div>
          </>
        ) : (
          <div className={`${styles.field} ${styles['field-full']}`}>
            <span className={styles.hint}>
              Dated <strong>{todayLabel}</strong> and created as <strong>Available</strong>.
            </span>
          </div>
        )}

      </form>
    </Modal>
  );
};

export default BlockTradeFormModal;
